export type SyncErrorKind =
  | "idempotent_success"
  | "occurrence_conflict"
  | "place_conflict"
  | "diagnosis_conflict"
  | "domain_conflict"
  | "quarantine"
  | "retry";

export type ClassifiableOp = {
  op:
    | "submit_care_entry"
    | "create_animal_case"
    | "create_animal_cases_batch"
    | "attach_media";
  clientId: string;
};

function asRecord(error: unknown): Record<string, unknown> {
  if (error && typeof error === "object") return error as Record<string, unknown>;
  return {};
}

function str(value: unknown): string {
  return value == null ? "" : String(value);
}

export function messageOf(error: unknown): string {
  if (error instanceof Error) return error.message;
  const rec = asRecord(error);
  return str(rec.message ?? rec.error_description ?? rec.hint);
}

export function classifySyncError(
  error: unknown,
  op: ClassifiableOp
): SyncErrorKind {
  const rec = asRecord(error);
  const message = messageOf(error);
  const lower = message.toLowerCase();
  const details = str(rec.details).toLowerCase();
  const hint = str(rec.hint).toLowerCase();
  const constraint = str(rec.constraint ?? rec.code).toLowerCase();
  const code = str(rec.code ?? rec.status);
  const blob = `${lower}\n${details}\n${hint}\n${constraint}`;

  if (blob.includes("occurrence_conflict") || blob.includes("regular_occ")) {
    return "occurrence_conflict";
  }
  if (blob.includes("place_conflict")) return "place_conflict";
  if (blob.includes("diagnosis_conflict")) return "diagnosis_conflict";

  if (
    code === "42501" ||
    code === "403" ||
    /not allowed|permission denied|care_write|not authenticated/.test(blob)
  ) {
    return "quarantine";
  }

  const isUnique =
    code === "23505" || /duplicate key|unique constraint|already exists/.test(blob);
  if (isUnique) {
    const expected =
      op.op === "submit_care_entry"
        ? "client_entry_id"
        : op.op === "attach_media"
          ? "client_media_id"
          : "client_create_id";
    const mentionsConstraint =
      blob.includes(expected) ||
      (op.op === "create_animal_cases_batch" && blob.includes("client_batch_id"));
    const mentionsOurId = blob.includes(op.clientId.toLowerCase());
    if (mentionsConstraint && mentionsOurId) return "idempotent_success";
    return "domain_conflict";
  }

  if (/failed to fetch|network request failed|fetch failed|timeout|econnreset/.test(blob)) {
    return "retry";
  }
  if (/^4\d\d$/.test(code) || code.startsWith("P") || code === "22P02") {
    return "domain_conflict";
  }
  return "retry";
}
